import CatalogoEquipos from "./pages/CatalogoEquipos";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { SecurityProvider, useSecurityContext } from "@/contexts/MaintenanceContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ChecklistProvider } from "@/contexts/ChecklistContext";
import { PartesTrabajoProvider } from "@/contexts/PartesTrabajoContext";
import { OrdenesTrabajoProvider } from "@/contexts/OrdenesTrabajoContext";
import { ObrasProvider } from "@/contexts/ObrasContext";
import { AusenciasProvider } from "@/contexts/AusenciasContext";
import PrivateRoute from "@/components/PrivateRoute";
import { UpdateNotification } from "@/components/UpdateNotification";
import { OfflineSyncManager } from "@/components/OfflineSyncManager";
import { AuthenticatedLayout } from "@/components/AuthenticatedLayout";
import { ConditionalLayout } from "@/components/ConditionalLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SystemsErrorBoundary } from "@/components/SystemsErrorBoundary";
import { ThemeProvider } from "@/components/ThemeProvider";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import ClientesWrapper from "./pages/ClientesWrapper";
import ClientesAbonados from "./pages/ClientesAbonados";
import Mantenimientos from "./pages/Mantenimientos";
import DetalleCliente from "./pages/DetalleCliente";
import SistemasCliente from "./pages/SistemasCliente";
import ElementosSistema from "./pages/ElementosSistema";
import ChecklistRevision from "./pages/ChecklistRevision";
import GestionUsuarios from "./pages/GestionUsuarios";

import Obras from "./pages/Obras";


import AltaClienteCRA from "./pages/AltaClienteCRA";
import MandatoSEPA from "./pages/MandatoSEPA";

import PartesTrabajo from "./pages/PartesTrabajo";
// OrdenesTorahajo removed - functionality merged into AsignacionTareas
import MiCalendarioPage from "./pages/MiCalendario";
import AsignacionTareas from "./pages/AsignacionTareas";
import GestionCodigosAnomalias from "./pages/GestionCodigosAnomalias";

import GestionEmpresas from "./pages/SuperAdmin/GestionEmpresas";
import FeedbackPanel from "./pages/SuperAdmin/FeedbackPanel";
import CatalogoGlobal from "./pages/SuperAdmin/CatalogoGlobal";
import ConfiguracionEmpresa from "./pages/ConfiguracionEmpresa";
import JornadasEquipo from "./pages/JornadasEquipo";
import Perfil from "./pages/Perfil";
import Herramientas from "./pages/Herramientas";
import RecepcionesMaterial from "./pages/RecepcionesMaterial";
import MisAusencias from "./pages/MisAusencias";
import AusenciasEquipo from "./pages/AusenciasEquipo";

import NotFound from "./pages/NotFound";
import FirmaContratoPublica from "./pages/FirmaContratoPublica";
import FirmaCertificadoPublica from "./pages/FirmaCertificadoPublica";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalLayout from "./pages/portal/PortalLayout";
import PortalInicio from "./pages/portal/PortalInicio";
import PortalDocumentos from "./pages/portal/PortalDocumentos";
import PortalDocumentosHub from "./pages/portal/PortalDocumentosHub";
import PortalMiCuenta from "./pages/portal/PortalMiCuenta";
import PortalAvisos from "./pages/portal/PortalAvisos";
import PortalHistorialOT from "./pages/portal/PortalHistorialOT";
import BandejaAvisos from "./pages/BandejaAvisos";
import PortalRoute from "./components/PortalRoute";

const queryClient = new QueryClient();

const SyncManagerWrapper = () => {
  const { sincronizarCambiosPendientes } = useSecurityContext();
  return <OfflineSyncManager onSyncRequest={sincronizarCambiosPendientes} />;
};

// Componente para manejar la ruta raíz
const RootRoute = () => {
  const { user, perfil, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  if (user) {
    if (perfil?.rol === 'cliente_portal') return <Navigate to="/portal" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Landing />;
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ChecklistProvider>
            <PartesTrabajoProvider>
              <OrdenesTrabajoProvider>
                <ObrasProvider>
                  <AusenciasProvider>
                  <SecurityProvider>
                      <BrowserRouter>
                      <SyncManagerWrapper />
                      <div className="fixed top-4 left-4 right-4 z-50">
                        <UpdateNotification />
                      </div>
                      <Routes>
                        {/* Public routes without sidebar */}
                        <Route path="/" element={<RootRoute />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/firma-contrato/:token" element={<FirmaContratoPublica />} />
                        <Route path="/firma-certificado/:token" element={<FirmaCertificadoPublica />} />
                        
                        {/* Portal del cliente */}
                        <Route path="/portal/login" element={<PortalLogin />} />
                        <Route path="/portal" element={<PortalRoute><PortalLayout /></PortalRoute>}>
                          <Route index element={<PortalInicio />} />
                          <Route path="documentos" element={<PortalDocumentosHub />} />
                          <Route path="avisos" element={<PortalAvisos />} />
                          <Route path="historial" element={<PortalHistorialOT />} />
                          <Route path="contratos" element={<Navigate to="/portal/documentos" replace />} />
                          <Route path="informes" element={<Navigate to="/portal/documentos" replace />} />
                          <Route path="cuenta" element={<PortalMiCuenta />} />
                        </Route>
                        
                        
                        
                        {/* Adaptive public forms - show sidebar if logged in */}
                        <Route path="/mandato-sepa" element={
                          <ConditionalLayout>
                            <MandatoSEPA />
                          </ConditionalLayout>
                        } />
                        <Route path="/alta-cliente-cra" element={
                          <ConditionalLayout>
                            <AltaClienteCRA />
                          </ConditionalLayout>
                        } />
                        
                        {/* Authenticated routes with sidebar */}
                        <Route path="/dashboard" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <Index />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/clientes" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <ClientesWrapper />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/clientes-abonados" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <ClientesAbonados />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/mantenimientos" element={
                          <PrivateRoute requireAnyRole={['admin', 'gestor']}>
                            <AuthenticatedLayout>
                              <Mantenimientos />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/cliente/:id" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <DetalleCliente />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/sistemas-cliente" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <SystemsErrorBoundary>
                                <SistemasCliente />
                              </SystemsErrorBoundary>
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/elementos-sistema" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <SystemsErrorBoundary>
                                <ElementosSistema />
                              </SystemsErrorBoundary>
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/checklist-revision" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <ChecklistRevision />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/gestion-usuarios" element={
                          <PrivateRoute requireRole="admin">
                            <AuthenticatedLayout>
                              <GestionUsuarios />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/partes-trabajo" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <PartesTrabajo />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        {/* Redirect old ordenes-trabajo to new unified page */}
                        <Route path="/ordenes-trabajo" element={<Navigate to="/asignacion-tareas" replace />} />
                        <Route path="/asignacion-tareas" element={
                          <PrivateRoute requireAnyRole={['admin', 'gestor']}>
                            <AuthenticatedLayout>
                              <AsignacionTareas />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/avisos" element={
                          <PrivateRoute requireAnyRole={['admin', 'gestor']}>
                            <AuthenticatedLayout>
                              <BandejaAvisos />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/mi-calendario" element={
                          <PrivateRoute requireAnyRole={['admin', 'tecnico']}>
                            <AuthenticatedLayout>
                              <MiCalendarioPage />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/obras" element={
                          <PrivateRoute requireAnyRole={['admin', 'gestor']}>
                            <AuthenticatedLayout>
                              <Obras />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/jornadas-equipo" element={
                          <PrivateRoute requireAnyRole={['admin', 'gestor']}>
                            <AuthenticatedLayout>
                              <JornadasEquipo />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/recepciones" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <RecepcionesMaterial />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/codigos-anomalias" element={
                          <PrivateRoute requireRole="admin">
                            <AuthenticatedLayout>
                              <GestionCodigosAnomalias />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/catalogo-equipos" element={
                          <PrivateRoute requireRole="admin">
                            <AuthenticatedLayout>
                              <CatalogoEquipos />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/super-admin/empresas" element={
                          <PrivateRoute requireRole="super_admin">
                            <AuthenticatedLayout>
                              <GestionEmpresas />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/super-admin/catalogo-global" element={
                          <PrivateRoute requireRole="super_admin">
                            <AuthenticatedLayout>
                              <CatalogoGlobal />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/admin/feedback" element={
                          <PrivateRoute requireRole="super_admin">
                            <AuthenticatedLayout>
                              <FeedbackPanel />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/herramientas" element={
                          <PrivateRoute requireRole="admin">
                            <AuthenticatedLayout>
                              <Herramientas />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/configuracion-empresa" element={
                          <PrivateRoute requireRole="admin">
                            <AuthenticatedLayout>
                              <ConfiguracionEmpresa />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/perfil" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <Perfil />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/mis-ausencias" element={
                          <PrivateRoute>
                            <AuthenticatedLayout>
                              <MisAusencias />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        <Route path="/ausencias-equipo" element={
                          <PrivateRoute requireAnyRole={['admin', 'gestor']}>
                            <AuthenticatedLayout>
                              <AusenciasEquipo />
                            </AuthenticatedLayout>
                          </PrivateRoute>
                        } />
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                      <Toaster />
                      <Sonner />
                    </BrowserRouter>
                 </SecurityProvider>
                 </AusenciasProvider>
               </ObrasProvider>
            </OrdenesTrabajoProvider>
          </PartesTrabajoProvider>
        </ChecklistProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;